import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Box, Link, Card } from '@mui/material';

import { useFormik } from 'formik';
import * as yup from 'yup';
import { DataGrid } from '@mui/x-data-grid';

// Ensure this path matches where your API service methods are defined
import { apiDocusign, apiget } from '../../service/api';

function DocuSignPage() {
  // Validation schema
  const [docuSignLink, setDocuSignLink] = useState('');
  const [envelopes, setEnvelopes] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');


  const fetchEnvelopes = async () => {
    try {
      const response = await apiget('docusign/list');
      if (response.status === 200) {
        // Ensure that the data is in the correct format for DataGrid
        const formattedData = response.data.result.map((item, index) => ({
          id: item._id, // DataGrid requires a field named 'id'
          title: item.title,
          envelopeId: item.envelopeId,
          status: item.status,
          sentDate: item.sentDate,
          completedDate: item.completedDate,
          documentPath: item.documentPath,
          signers: item.signers.map(signer => `${signer.name} (${signer.email})`).join(', '),
          createdBy: item.createdBy,
        }));
        setEnvelopes(formattedData);
      }
    } catch (error) {
      console.error('Failed to fetch envelopes:', error);
    }
  };

  useEffect(() => {
    fetchEnvelopes();
  }, []);


  const onRetrieve = async (envelopeId) => {
    try {
      const response = await apiget(`docusign/retrieve/${envelopeId}`);
      // Assuming your backend responds with the envelope data
      // console.log('Envelope retrieved:', response.data);
      setAlertMessage('Envelope retrieved successfully.'); // Display some feedback
    } catch (error) {
      console.error('Error retrieving envelope:', error);
      setAlertMessage('Failed to retrieve envelope. Please try again.'); // Display error feedback
    }
  };

  const columns = [
    
    { field: 'title', headerName: 'Title', flex: 1 },
    { field: 'envelopeId', headerName: 'Envelope ID', flex: 1 },
    { field: 'status', headerName: 'Status', flex: 1 },
    { field: 'sentDate', headerName: 'Sent Date', flex: 1, type: 'dateTime' },
    { field: 'completedDate', headerName: 'Completed Date', flex: 1, type: 'dateTime' },
    // { field: 'documentPath', headerName: 'Document Path', flex: 1 },
    { field: 'signers', headerName: 'Signers', flex: 1 },
    { field: 'actions', headerName: 'Actions', flex: 1, sortable: false, 
    renderCell: (params) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" size="small" onClick={() => console.log("Resend", params.row.envelopeId)}>Resend</Button>
        <Button variant="outlined" size="small" onClick={() => onRetrieve(params.row.envelopeId)}>Retrieve</Button>
      </Box>
    ),
  },
];

  const userid = localStorage.getItem('user_id')
  const validationSchema = yup.object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().email('Enter a valid email').required('Email is required'),
    file: yup.mixed().required('A file is required'),
  });

  // Formik hook for form state and handling
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      file: null,
      createdBy: userid
    },
    // validationSchema: validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);
      formData.append('email', values.email);
      formData.append('file', values.file);
      formData.append('title', 'Document Title');
      formData.append('createdBy', userid);

      try {
        const response = await apiDocusign('docusign/create-envelope', formData);
        
        if (response.data && response.data.docusignUrl) {
          window.location.href = response.data.docusignUrl;
        }

        if (response.status === 200 || response.status === 201) {
          console.log('Success:', response.data);
          alert('Document sent for signing successfully!');
        } else {
          console.error('Failed to send document. Server responded with:', response.status);
          alert('Failed to send document. Please try again.');
        }
      } catch (error) {
        console.error('Error sending document:', error);
        alert('An error occurred while sending the document. Please try again.');
      }
    },
  });
  return (
    <>
    <Container sx={{ marginBottom: 4 }}>
      <Typography variant="h4" sx={{ marginY: 2 }}>
        Sent DocuSign Envelopes
      </Typography>
      <Card style={{ height: 500, width: '100%' }}>
        <DataGrid rows={envelopes} columns={columns} pageSize={5} />
      </Card>
    </Container>

    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Send Document via DocuSign
        </Typography>
        <Typography variant="body1">
          Fill in your details and upload a document to send through DocuSign.
        </Typography>
        {/* Display the DocuSign link if available */}
        {docuSignLink && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body1">Document sent! You can sign it here:</Typography>
            <Link href={docuSignLink} target="_blank" rel="noopener noreferrer">
              Sign Document
            </Link>
          </Box>
        )}
      </Box>

      <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ mt: 1 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="firstName"
          label="First Name"
          name="firstName"
          autoComplete="fname"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
          helperText={formik.touched.firstName && formik.errors.firstName}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="lastName"
          label="Last Name"
          name="lastName"
          autoComplete="lname"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
          helperText={formik.touched.lastName && formik.errors.lastName}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
        <input
          accept="application/pdf"
          style={{ display: 'none' }}
          id="file"
          name="file"
          type="file"
          onChange={(event) => {
            formik.setFieldValue("file", event.currentTarget.files[0]);
          }}
        />
        <label htmlFor="file"> {/* Matches the input id */}
          <Button variant="contained" component="span" sx={{ mt: 3, mb: 2 }}>
            Upload File
          </Button>
        </label>
        {formik.values.file && <Typography>{formik.values.file.name}</Typography>}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          Send Document
        </Button>
      </Box>
    </Container>
    </>
  );
}

export default DocuSignPage;
